import { Link } from 'react-router-dom';
import { Bell, Calendar, MessageCircle, Sparkles, Star, type LucideIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useNotifications, useMarkAllNotificationsRead } from '@/features/marketplace/hooks/useNotifications';
import { brand } from '@/lib/theme/tokens';
import { cn } from '@/lib/utils/cn';

const ICONS: Record<string, LucideIcon> = {
  booking: Calendar,
  message: MessageCircle,
  review: Star,
  system: Sparkles,
  streak: Sparkles,
  reward: Star,
};

const COLORS: Record<string, string> = {
  booking: brand.ok,
  message: brand.teal,
  review: brand.warn,
  system: brand.saffron,
  streak: brand.chinar,
  reward: brand.saffron,
};

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function NotificationsDropdown() {
  const { data: notifications = [] } = useNotifications({ per_page: 5 });
  const markAllRead = useMarkAllNotificationsRead();

  const unread = notifications.filter((n) => !n.is_read).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label={`Notifications${unread ? ` · ${unread} new` : ''}`}
          className="relative h-9 w-9 rounded-full p-0"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold text-white"
              style={{
                background: brand.chinar,
                boxShadow: '0 0 12px rgba(196,90,42,0.5)',
              }}
            >
              {unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[320px] !bg-[var(--bg)] shadow-[0_20px_60px_rgba(0,0,0,0.35),0_4px_16px_rgba(0,0,0,0.2)]"
      >
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="px-1">Notifications</DropdownMenuLabel>
          <button
            type="button"
            className="text-[11px] font-semibold"
            style={{ color: brand.teal }}
            onClick={() => markAllRead.mutate()}
          >
            Mark all read
          </button>
        </div>

        <DropdownMenuSeparator />

        <div className="max-h-[360px] overflow-y-auto" data-lenis-prevent>
          {notifications.map((n) => {
            const Icon = ICONS[n.kind] ?? Sparkles;
            const color = COLORS[n.kind] ?? brand.teal;
            return (
              <div
                key={n.id}
                className={cn(
                  'flex items-start gap-2.5 rounded-lg px-2 py-2.5 transition-colors hover:bg-[var(--glass-hi)]',
                )}
              >
                <span
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-[8px]"
                  style={{ background: `${color}22`, border: `1px solid ${color}40` }}
                >
                  <Icon size={13} color={color} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[12px] font-semibold text-ink">
                      {n.title}
                    </span>
                    {!n.is_read && (
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ background: brand.teal }}
                      />
                    )}
                  </div>
                  <div className="mt-0.5 line-clamp-2 text-[11px] text-ink-dim">{n.body}</div>
                </div>
                <span className="shrink-0 text-[10px] text-ink-mute">{formatRelative(n.created_at)}</span>
              </div>
            );
          })}
        </div>

        <DropdownMenuSeparator />

        <Link
          to="/notifications"
          className="block rounded-lg px-3 py-2 text-center text-[12px] font-semibold transition-colors hover:bg-[var(--glass-hi)]"
          style={{ color: brand.teal }}
        >
          View all notifications →
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
